import { useEffect, useRef } from 'react';
import type { ServiceCatalog } from '@obraplan/core/domain/takeoff/service-catalog';
import { chaveDaFoto, fotoDaMedida, type FotoDaMedida } from './foto-da-medida';
import type { TypologyDraft, WorkspaceDraft } from './typology-draft';

/**
 * Quanto a bancada fica parada antes da foto da medida: a conta da planta de todas as tipologias
 * não é barata, e ninguém precisa da foto a cada tecla — precisa dela depois que o desenho para.
 */
const ESPERA_MS = 4000;

/**
 * Grava a foto da medida da obra aberta quando ela muda (Fase 40, RN-516, ADR-182).
 *
 * A foto é derivada do desenho, do catálogo e das decisões da tipologia, que gravam cada um pela
 * sua porta — por isso ela tem a dela, e sobe sozinha depois que a bancada para. A mesma foto não
 * sobe de novo. `gravar` devolve se o servidor aceitou: recusada, a próxima pausa tenta outra vez.
 */
export function useFotoDaMedida(opcoes: {
  readonly workspace: WorkspaceDraft;
  readonly recorteDe: (typology: TypologyDraft) => ServiceCatalog;
  readonly projectId: string | undefined;
  /** A bancada carregada, sem conflito, numa obra — senão, nada sobe. */
  readonly ativo: boolean;
  readonly gravar: (unitTypes: FotoDaMedida) => Promise<boolean>;
}): void {
  const { workspace, recorteDe, projectId, ativo, gravar } = opcoes;
  const enviada = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!ativo || projectId === undefined) return;
    const temporizador = setTimeout(() => {
      const foto = fotoDaMedida(workspace, recorteDe);
      if (foto.length === 0) return;
      const chave = `${projectId}:${chaveDaFoto(foto)}`;
      if (chave === enviada.current) return;
      void gravar(foto).then((aceita) => {
        if (aceita) enviada.current = chave;
      });
    }, ESPERA_MS);
    return () => clearTimeout(temporizador);
  }, [ativo, projectId, workspace, recorteDe, gravar]);
}
